/**
 * personalization-events.js
 * Viori Personalizer — Phase 1: Event Stream
 *
 * Captures visitor interactions and streams them to the Cloudflare Worker
 * endpoint (/events). Events are batched in memory and flushed every 5
 * seconds or when 10 events accumulate, whichever comes first.
 * On page unload, remaining events are flushed via navigator.sendBeacon().
 *
 * Depends on: personalization-state.js (must load first)
 *
 * Public API:
 *   VioriEvents.track(eventType, payload?) → void
 *   VioriEvents.flush()                    → void (force-send batch)
 *
 * Event types (spec Section 3j):
 *   session_start       — fired once per session
 *   state_change        — fired by VioriState.on('state_change', ...)
 *   quiz_start          — visitor opens quiz
 *   quiz_step           — visitor answers a quiz question
 *   quiz_complete       — visitor finishes quiz
 *   product_view        — product page viewed
 *   collection_view     — collection page viewed
 *   add_to_cart         — product added to cart
 *   popup_shown         — personalized popup displayed
 *   popup_accepted      — visitor accepted popup offer
 *   popup_dismissed     — visitor dismissed popup
 *   faq_expand          — FAQ item opened
 *   review_read         — review section scrolled into view
 *   homepage_interaction— hair goal / hair type / concern section clicked
 *
 * Spec refs: Section 3c, 3j
 */

(function (root) {
  'use strict';

  // ── Config ─────────────────────────────────────────────────────────────────
  // The endpoint URL is injected by Liquid via a global set before this script:
  //   <script>window.VIORI_EVENT_ENDPOINT = '{{ settings.event_endpoint }}';</script>
  var ENDPOINT         = root.VIORI_EVENT_ENDPOINT || null;
  var BATCH_SIZE       = 10;   // max events before forced flush (spec: 10)
  var BATCH_INTERVAL   = 5000; // ms between auto-flushes (spec: 5 seconds)
  var RATE_LIMIT       = 100;  // max events per session per minute (spec Section 3j)
  var RATE_WINDOW      = 60000; // 1 minute in ms

  // ── Internal State ─────────────────────────────────────────────────────────
  var _queue        = [];
  var _timer        = null;
  var _sessionStart = false;
  var _rateCount    = 0;
  var _rateReset    = Date.now() + RATE_WINDOW;

  // ── Helpers ────────────────────────────────────────────────────────────────
  function _getState() {
    // Defensive: VioriState may not be loaded if script order is wrong
    return root.VioriState || null;
  }

  function _sessionId() {
    var vs = _getState();
    return vs ? vs.getSessionId() : 'unknown';
  }

  function _deviceType() {
    var ua = navigator.userAgent || '';
    if (/Mobi|Android|iPhone|iPad/i.test(ua)) return 'mobile';
    if (/Tablet|iPad/i.test(ua)) return 'tablet';
    return 'desktop';
  }

  function _pageUrl() {
    try { return window.location.href; } catch (e) { return ''; }
  }

  function _referrer() {
    try { return document.referrer || ''; } catch (e) { return ''; }
  }

  function _now() {
    return Date.now();
  }

  // ── Rate Limiter ───────────────────────────────────────────────────────────
  function _rateAllowed() {
    var now = _now();
    if (now > _rateReset) {
      _rateCount = 0;
      _rateReset = now + RATE_WINDOW;
    }
    if (_rateCount >= RATE_LIMIT) {
      // Log once per minute, not per dropped event
      if (_rateCount === RATE_LIMIT) {
        _track_internal('rate_limit_exceeded', { limit: RATE_LIMIT });
      }
      return false;
    }
    _rateCount++;
    return true;
  }

  // ── Event Builder ──────────────────────────────────────────────────────────
  function _buildEvent(eventType, payload) {
    var vs = _getState();
    var base = {
      session_id:  _sessionId(),
      event_type:  eventType,
      timestamp:   _now(),
      page_url:    _pageUrl(),
    };
    // Attach persona snapshot to every event for cohort analysis
    if (vs) {
      base.persona = {
        hair_type:     vs.getState('quiz_hairType'),
        hair_goals:    vs.getState('quiz_hairGoals'),
        hair_concerns: vs.getState('quiz_hairConcerns'),
        who_for:       vs.getState('quiz_whoIsThisFor'),
        quiz_complete: vs.getState('quiz_quizComplete'),
      };
    }
    return Object.assign(base, payload || {});
  }

  // ── Queue & Flush ──────────────────────────────────────────────────────────
  function _enqueue(event) {
    _queue.push(event);
    if (_queue.length >= BATCH_SIZE) {
      flush();
    }
  }

  function _send(events) {
    if (!events || events.length === 0) return;
    if (!ENDPOINT) {
      // No endpoint configured yet — log to console in dev, swallow in prod
      if (root.__VIORI_DEV__) {
        console.debug('[VioriEvents] No endpoint configured. Events:', events);
      }
      return;
    }

    var body = JSON.stringify({ events: events });

    // Try fetch first (modern browsers, non-unload context)
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true, // allows request to outlive the page
      }).catch(function () { /* fire-and-forget: swallow network errors */ });
      return;
    } catch (e) { /* fetch not available */ }

    // Fallback: sendBeacon (always available in modern browsers)
    try {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
    } catch (e) { /* swallow */ }
  }

  function flush() {
    if (_queue.length === 0) return;
    var batch = _queue.slice();
    _queue = [];
    _send(batch);
  }

  function _startTimer() {
    if (_timer) return;
    _timer = setInterval(flush, BATCH_INTERVAL);
  }

  // ── Internal track (bypasses rate limiter for system events) ───────────────
  function _track_internal(eventType, payload) {
    _enqueue(_buildEvent(eventType, payload));
  }

  // ── Public track ───────────────────────────────────────────────────────────
  function track(eventType, payload) {
    if (!_rateAllowed()) return;
    _enqueue(_buildEvent(eventType, payload));
  }

  // ── Session Start ──────────────────────────────────────────────────────────
  function _fireSessionStart() {
    if (_sessionStart) return; // only once per page load
    _sessionStart = true;
    var vs = _getState();
    _track_internal('session_start', {
      landing_page:  _pageUrl(),
      referrer:      _referrer(),
      device_type:   _deviceType(),
      utm_source:    vs ? vs.getState('utm_source')   : null,
      utm_medium:    vs ? vs.getState('utm_medium')   : null,
      utm_campaign:  vs ? vs.getState('utm_campaign') : null,
    });
  }

  // ── State Change Listener ──────────────────────────────────────────────────
  // Mirrors every VioriState.setState() call into the event stream.
  // This is how quiz answers, homepage interactions, and product views
  // all flow to the behavioral cohort engine automatically.
  function _attachStateChangeListener() {
    var vs = _getState();
    if (!vs) return;
    vs.on('state_change', function (data) {
      // Skip internal meta keys — they're not meaningful cohort signals
      if (data.key && data.key.indexOf('meta_') === 0) return;
      track('state_change', {
        key:   data.key,
        alias: data.alias,
        value: data.value,
      });
    });
    vs.on('ls_unavailable', function () {
      _track_internal('ls_unavailable', {});
    });
    vs.on('state_reset', function (data) {
      _track_internal('state_reset', data);
    });
  }

  // ── Page-Level Auto-Tracking ───────────────────────────────────────────────
  // Detects which page type we're on and fires the appropriate event.
  function _autoTrackPage() {
    var url = _pageUrl();
    if (/\/products\//.test(url)) {
      track('product_view', {
        handle: (url.match(/\/products\/([^/?#]+)/) || [])[1] || null,
      });
      // Also push to productsViewed state
      var handle = (url.match(/\/products\/([^/?#]+)/) || [])[1];
      if (handle && _getState()) {
        _getState().setState('browse_productsViewed', handle);
      }
    } else if (/\/collections\//.test(url)) {
      track('collection_view', {
        collection: (url.match(/\/collections\/([^/?#]+)/) || [])[1] || null,
      });
    }
  }

  // ── Unload Beacon ──────────────────────────────────────────────────────────
  function _attachUnloadFlush() {
    // visibilitychange is more reliable than beforeunload on mobile
    try {
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') {
          // Use sendBeacon directly so it survives page close
          if (_queue.length === 0) return;
          var batch = _queue.slice();
          _queue = [];
          if (!ENDPOINT) return;
          try {
            var body = JSON.stringify({ events: batch });
            navigator.sendBeacon(ENDPOINT,
              new Blob([body], { type: 'application/json' }));
          } catch (e) { /* swallow */ }
        }
      });
    } catch (e) { /* ignore */ }

    // Also catch beforeunload for desktop browsers
    try {
      window.addEventListener('beforeunload', function () {
        flush();
      });
    } catch (e) { /* ignore */ }
  }

  // ── Convenience Trackers (called by Liquid section JS) ────────────────────

  function trackQuizStart() {
    track('quiz_start', {});
  }

  function trackQuizStep(step, key, value) {
    track('quiz_step', { step: step, key: key, value: value });
  }

  function trackQuizComplete() {
    track('quiz_complete', {
      hair_type:     _getState() ? _getState().getState('quiz_hairType') : null,
      hair_goals:    _getState() ? _getState().getState('quiz_hairGoals') : null,
      hair_concerns: _getState() ? _getState().getState('quiz_hairConcerns') : null,
    });
  }

  function trackPopup(action, offerType) {
    // action: 'shown' | 'accepted' | 'dismissed'
    track('popup_' + action, { offer_type: offerType || null });
  }

  function trackHomepageInteraction(section, value) {
    track('homepage_interaction', { section: section, value: value });
  }

  function trackFAQExpand(question) {
    track('faq_expand', { question: question });
  }

  function trackReviewRead(reviewId, chunkKey) {
    track('review_read', { review_id: reviewId, chunk_key: chunkKey });
  }

  function trackAddToCart(handle, variantId, quantity) {
    track('add_to_cart', {
      handle: handle,
      variant_id: variantId,
      quantity: quantity || 1,
    });
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  function _init() {
    // Wait for VioriState to be ready (it loads before this)
    _attachStateChangeListener();
    _startTimer();
    _fireSessionStart();
    _autoTrackPage();
    _attachUnloadFlush();
  }

  // DOM-ready guard: run immediately if DOM is ready, else defer
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', _init);
  } else {
    _init();
  }

  // ── Public Interface ───────────────────────────────────────────────────────
  root.VioriEvents = {
    track:                    track,
    flush:                    flush,
    // Convenience trackers (called from section JS)
    trackQuizStart:           trackQuizStart,
    trackQuizStep:            trackQuizStep,
    trackQuizComplete:        trackQuizComplete,
    trackPopup:               trackPopup,
    trackHomepageInteraction: trackHomepageInteraction,
    trackFAQExpand:           trackFAQExpand,
    trackReviewRead:          trackReviewRead,
    trackAddToCart:           trackAddToCart,
  };

}(window));
